import { Box, Flex, Icon, Text, Tooltip } from "@chakra-ui/react";
import { IconType } from "react-icons";
import {
  FaCalculator,
  FaFileSignature,
  FaFingerprint,
  FaHandshake,
  FaMoneyBillWave,
  FaUniversity,
  FaUserClock,
} from "react-icons/fa";
import { AuctionProposalStatus } from "@/models/credit-auction-proposal.model";
import { AUCTION_PROPOSAL_STATUS_LABELS } from "@/utils/auctionProposalStatus";

interface StepDefinition {
  status: AuctionProposalStatus;
  icon: IconType;
}

// A modal só exibe propostas que venceram o leilão, então o fluxo começa
// direto no "Leilão vencido BMP" (CHOSEN) — os passos anteriores (proposta
// ofertada / em leilão) são ocultados para economizar espaço.
const HAPPY_PATH_STEPS: StepDefinition[] = [
  { status: AuctionProposalStatus.CHOSEN, icon: FaHandshake },
  { status: AuctionProposalStatus.PENDING_BANK_ANALYSIS, icon: FaUniversity },
  { status: AuctionProposalStatus.PENDING_CONFIRMATION, icon: FaFingerprint },
  // Status do redesign que ocorrem durante a contratação — agora com nó próprio.
  { status: AuctionProposalStatus.PROCESSING_SIMULATION, icon: FaCalculator },
  { status: AuctionProposalStatus.AWAITING_BORROWER_CHOICE, icon: FaUserClock },
  { status: AuctionProposalStatus.PENDING_FORMALIZATION, icon: FaFileSignature },
  { status: AuctionProposalStatus.PAYED, icon: FaMoneyBillWave },
];

// Status de erro/pendência → passo do happy path que fica em vermelho.
// Mapeado por status (não índice fixo) para sobreviver a mudanças de ordem.
const ERROR_STATUS_STEP: Partial<
  Record<AuctionProposalStatus, AuctionProposalStatus>
> = {
  [AuctionProposalStatus.REPROVED]: AuctionProposalStatus.PENDING_BANK_ANALYSIS,
  [AuctionProposalStatus.REPROVED_IN_PROCESS]:
    AuctionProposalStatus.PROCESSING_SIMULATION,
  // "Falha no link da formalização" — ocorre na fase de Formalização (criação
  // do link Unico/biometria), não no início. O erro deve cair em Formalização.
  [AuctionProposalStatus.ERROR_CREATE_BIOMETRIC_VALIDATION]:
    AuctionProposalStatus.PENDING_FORMALIZATION,
  [AuctionProposalStatus.PENDING_USER_REVIEW]:
    AuctionProposalStatus.PENDING_FORMALIZATION,
  [AuctionProposalStatus.PENDING_REFORMALIZATION]:
    AuctionProposalStatus.PENDING_FORMALIZATION,
  [AuctionProposalStatus.IN_CANCELLATION]:
    AuctionProposalStatus.PENDING_FORMALIZATION,
  [AuctionProposalStatus.CANCELED]: AuctionProposalStatus.PENDING_FORMALIZATION,
  [AuctionProposalStatus.CANCELED_BY_CLIENT]:
    AuctionProposalStatus.PENDING_FORMALIZATION,
};

type StepState = "completed" | "current" | "error" | "future";

const STEP_COLORS: Record<StepState, { bg: string; color: string; line: string }> = {
  completed: { bg: "green.100", color: "green.500", line: "green.100" },
  current: { bg: "green.500", color: "white", line: "gray.300" },
  error: { bg: "red.500", color: "white", line: "gray.300" },
  future: { bg: "gray.100", color: "gray.400", line: "gray.300" },
};

interface StatusFlowProps {
  currentStatus: AuctionProposalStatus;
}

export default function StatusFlow({ currentStatus }: StatusFlowProps) {
  const happyPathIndex = HAPPY_PATH_STEPS.findIndex(
    (step) => step.status === currentStatus
  );
  const isOnHappyPath = happyPathIndex !== -1;
  const errorStatus = ERROR_STATUS_STEP[currentStatus];
  const errorIndex = isOnHappyPath
    ? -1
    : errorStatus
    ? HAPPY_PATH_STEPS.findIndex((step) => step.status === errorStatus)
    : 0;

  const getStepState = (index: number): StepState => {
    if (isOnHappyPath) {
      if (index < happyPathIndex) return "completed";
      if (index === happyPathIndex) return "current";
      return "future";
    }
    if (index < errorIndex) return "completed";
    if (index === errorIndex) return "error";
    return "future";
  };

  return (
    <Flex
      align="flex-start"
      justify="center"
      wrap="nowrap"
      gap={0}
      w="100%"
      overflowX="auto"
    >
      {HAPPY_PATH_STEPS.map((step, index) => {
        const state = getStepState(index);
        const colors = STEP_COLORS[state];
        const isLast = index === HAPPY_PATH_STEPS.length - 1;
        const fullLabel =
          state === "error"
            ? AUCTION_PROPOSAL_STATUS_LABELS[currentStatus] ?? currentStatus
            : AUCTION_PROPOSAL_STATUS_LABELS[step.status] ?? step.status;
        const labelColor =
          state === "current"
            ? "#1E293B"
            : state === "error"
            ? "red.500"
            : "#64748B";
        const labelWeight = state === "current" ? "500" : "normal";

        return (
          <Flex key={step.status} align="flex-start">
            <Flex direction="column" align="center" gap={1} w="72px">
              <Tooltip label={fullLabel} placement="top" hasArrow>
                <Flex
                  w={9}
                  h={9}
                  bg={colors.bg}
                  color={colors.color}
                  borderRadius="full"
                  align="center"
                  justify="center"
                  cursor="pointer"
                  transition="transform 0.15s ease"
                  _hover={{ transform: "scale(1.15)" }}
                  boxShadow={state === "current" ? "md" : "none"}
                  flexShrink={0}
                >
                  <Icon as={step.icon} boxSize={4} />
                </Flex>
              </Tooltip>
              <Text
                fontFamily="Lexend Deca"
                fontSize="11px"
                fontWeight={labelWeight}
                color={labelColor}
                textAlign="center"
                lineHeight="1.15"
                noOfLines={2}
              >
                {fullLabel}
              </Text>
            </Flex>
            {!isLast && (
              <Flex w={3} h={9} align="center" flexShrink={0}>
                <Box w="100%" h="2px" bg={colors.line} />
              </Flex>
            )}
          </Flex>
        );
      })}
    </Flex>
  );
}
